import { Box, Divider, Stack, Typography } from '@mui/material';

import { Button } from '@karehero/llama/components/atoms/Button/Button';

import { EmailClient } from '@/cam-admin/explore/paywall/Flow';
import { FLOW } from '@/cam-admin/explore/paywall/Paywall.content';
import { type Shortlists } from '@/cam-admin/paywall/useShortlists';
import { PUBLISH } from '@/cam-admin/paywall/Shortlist.content';

// What the provider gets, before it goes.
//
// A full screen rather than a second dialog. A CAM has just edited four fields
// and cannot tell from the fields whether the email reads like a person wrote it,
// which is the only question this screen answers — and a dialog stacked on a
// dialog would leave them two Cancels deep.
//
// It renders inside the real email chrome from the flow (`EmailClient`) rather
// than a mock of one, and reads its furniture — greeting, link, sign-off, footer
// — from `FLOW.email`. Only the paragraph about the request is composed here, so
// the preview cannot drift from the email that ships: if that copy changes, this
// changes with it.

// The request, as sentences. The four fields a CAM just edited, read back the way
// a provider will meet them: prose rather than a labelled table, because this is an
// email from a person and a spec sheet in an inbox reads as an advert.
//
// Nothing here is lowercased. Running the fields through toLowerCase turned DOM care
// into "dom care" and Alzheimer's into "alzheimer's", which is the preview telling a
// CAM we will misspell a condition in front of every provider in the area.
const andList = (items: string[]) =>
  items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;

const requestLines = ({
  careType,
  needs,
  funding,
  area,
  startTiming,
}: {
  careType: string;
  needs: string[];
  funding: string;
  area: string;
  startTiming: string;
}) =>
  [
    `We have a ${careType} client in ${area} who matches your service. They are ${funding.toLowerCase()} and looking to start ${startTiming.toLowerCase()}.`,
    needs.length ? `Their needs are ${andList(needs)}.` : '',
  ].filter(Boolean);

const SendPreview = ({ shortlists }: { shortlists: Shortlists }) => {
  const { providers, sendDraft, isPreviewOpen, closePreview } = shortlists;

  if (!isPreviewOpen || sendDraft === null) return null;

  const c = FLOW.email;
  // Addressed to the first provider on the list. One link per provider means
  // every copy of this differs by the name at the top, and showing it addressed
  // to nobody would hide the one thing a CAM might want to check.
  const first = providers[0];

  return (
    // Covers the workspace, in the local DOM so it stays inside the laptop frame
    // — same reasoning as the row drawer, which is why this is absolute rather
    // than a portal.
    <Box sx={{ position: 'absolute', zIndex: 20, inset: 0 }}>
      <Stack sx={{ height: '100%', bgcolor: 'background.default' }}>
        {/* The only KareHero furniture on the screen. It has to be outside the
            email, because the moment our own chrome sits inside that rectangle
            the preview stops being a preview. */}
        <Stack
          sx={{
            alignItems: 'center',
            px: 6,
            py: 3,
            borderBottom: 1,
            borderColor: 'custom.neutral.200',
            flexShrink: 0,
          }}
          direction='row'
          spacing={2}
        >
          <Typography sx={{ flex: 1 }} variant='h3'>
            {PUBLISH.previewTitle}
          </Typography>
          <Button
            ariaLabel={PUBLISH.previewBack}
            variant='secondary'
            size='sm'
            onPress={closePreview}
          >
            {PUBLISH.previewBack}
          </Button>
        </Stack>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <EmailClient
            subject={c.subject}
            from={c.fromName}
            fromAddress={c.fromAddress}
            to={first?.email ?? c.to}
            received={c.received}
          >
            <Stack sx={{ maxWidth: 720 }} spacing={3}>
              <Typography color='inherit'>
                {first ? `Hello ${first.name},` : c.greeting}
              </Typography>
              {requestLines(sendDraft).map((line) => (
                <Typography key={line} color='inherit'>
                  {line}
                </Typography>
              ))}
              {/* The CAM's own paragraph, in the body rather than appended after
                  the link: it is context for the decision, so it has to be read
                  before the thing that asks for one. */}
              {sendDraft.extra.trim() && (
                <Typography color='inherit'>{sendDraft.extra}</Typography>
              )}
              <Typography color='inherit'>{c.body[1]}</Typography>
              <Typography color='inherit' sx={{ textDecoration: 'underline' }}>
                {c.cta}
              </Typography>
              <Stack>
                {c.signOff.map((l) => (
                  <Typography key={l} color='inherit'>
                    {l}
                  </Typography>
                ))}
              </Stack>
              <Divider />
              {/* Composed rather than read from `FLOW.email.foot`, which names the
                  one provider the sketch was addressed to. In a preview a CAM is
                  checking the details, and a footer about somebody else's care home
                  is the detail they would catch. */}
              <Typography sx={{ fontSize: 13 }} color='grey.700'>
                {PUBLISH.previewFoot(
                  first?.name ?? 'your service',
                  sendDraft.careType,
                )}
              </Typography>
            </Stack>
          </EmailClient>
        </Box>
      </Stack>
    </Box>
  );
};

SendPreview.displayName = 'SendPreview';
export default SendPreview;
